import React, { FC, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Modal,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import colors from "../../theme/Colours";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  addressCardContainer,
  card,
  cardBody,
  cardStyle,
  headerA,
  headerB,
  headerBorderBottom,
  headerStyle,
  stepperStyles,
} from "./orderDetailsStyles";
import { OrderDetails as OrderDetailsProps } from "../../dto/orderSummaryTypes";
import { orderSummaryStages } from "../../constants/orderSummary";
import { AddressBody, AddressHeader } from "./AddressCard";
import PaymentCard from "./PaymentCard";
import { StepIcon } from "./OrderStepsBar";
import { ActionButton } from "../actionButton/ActionButton";
import { useRole } from "../../context/RoleContext";
import ConfirmationDialog from "../confirmationDialog/ConfirmationDialog";
import Toast from "../Toast";
import { getOrderById, triggerOrderPayment } from "../../api/orderFinalAPI";

type OrderDetailsPropsExtended = OrderDetailsProps & {
  currentStage: number;
  isOrderCancelled?: boolean;
};

const OrderDetails: FC<OrderDetailsPropsExtended> = ({
  id,
  date,
  orders,
  billingAddress,
  shippingAddress,
  payment,
  currentStage,
  isOrderCancelled = false,
}) => {
  const orderId = location.pathname.split("/").pop();
  const { role } = useRole();
  const [transactionId, setTransactionId] = useState("");
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string>("");
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const customerName = billingAddress?.name || "";

  const productsLength = orders.length;
  const headerItems = [
    {
      title: `#${id}`,
      style: headerA,
    },
    {
      title: `${date}`,
      style: headerB,
    },
    {
      title: `${productsLength} Product${productsLength !== 1 ? "s" : ""}`,
      style: headerB,
    },
  ];
  const isSellerPortal = () => {
    if (
      role === "maltora-seller" ||
      location.pathname.startsWith("/maltora-seller")
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await getOrderById(orderId);
        setCustomerNote(response.data.data.payment.customerNote || "");
        if (response.data.data.payment.transactionId) {
          setTransactionId(response.data.data.payment.transactionId);
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const showPaymentReceipt = async () => {
    try {
      const response = await getOrderById(orderId);

      if (response.data) {
        const receiptUrl = response?.data?.data?.receiptUrl;
        if (receiptUrl) {
          setReceiptUrl(receiptUrl);
          setShowReceiptModal(true);
          setToastMessage("Fetched Receipt Successfully");
          setShowToast(true);
          setError(false);
        } else {
          setToastMessage("No receipt found");
          setShowToast(true);
          setError(true);
        }
      }
    } catch (error: any) {
      setToastMessage(
        error?.response?.data?.error?.message ?? "No receipt found"
      );
      setShowToast(true);
      setError(true);
    }
  };

  const triggerPayment = async () => {
    setShowPopUp(false);
    try {
      await triggerOrderPayment(orderId);
      setToastMessage(`An email has been sent to ${customerName}`);
      setError(false);
      setShowToast(true);
    } catch (error) {
      console.error("triggerPayment failed:", error);
      setToastMessage("Failed to trigger payment");
      setError(true);
      setShowToast(true);
    }
  };

  return (
    <Card sx={card}>
      <CardHeader
        title={
          <Box sx={headerStyle}>
            {headerItems.map((item, idx) => (
              <>
                <Typography key={`title_${idx}`} sx={item.style}>
                  {item.title}
                </Typography>
                {idx < headerItems.length - 1 && (
                  <Typography
                    key={`separator_${idx}`}
                    sx={[headerB, { margin: "0 10px" }]}
                  >
                    •
                  </Typography>
                )}
              </>
            ))}
          </Box>
        }
        sx={{
          ...headerBorderBottom,
          display: { xs: "none", sm: "block" },
        }}
      />
      <CardContent sx={cardBody}>
        <Box sx={addressCardContainer}>
          <Card sx={cardStyle}>
            <AddressHeader title="Shipping Address" />
            <AddressBody
              {...(shippingAddress?.address ? shippingAddress : billingAddress)}
            />
          </Card>
        </Box>
        <PaymentCard {...payment} />
      </CardContent>

      <Stepper
        sx={{
          ...stepperStyles.stepper,
          "& .MuiStepConnector-line": {
            borderWidth: { xs: "5px", sm: "8px" },
          },
          "& .MuiStepConnector-root": {
            top: { xs: "10px", sm: "20px" },
            left: "calc(-50% + 10px)",
            right: "calc(50% + 10px)",
            position: "absolute",
            width: "100%",
          },
        }}
        activeStep={currentStage}
        alternativeLabel
      >
        {orderSummaryStages.map((label) => (
          <Step key={label.title}>
            <StepLabel
              slots={{
                stepIcon: StepIcon,
              }}
              sx={stepperStyles.stepperLabel}
            >
              {label.title}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      {/* Buttons with ActionButton */}
      {isSellerPortal() && (
        <Box
          sx={{
            display: "flex",
            justifyContent: transactionId ? "space-between" : "end",
            alignItems: "center",
            gap: 2,
            margin: 2,
          }}
        >
          {transactionId && (
            <div>
              <p
                style={{
                  fontSize: "20px",
                  fontWeight: "500",
                  margin: 0,
                }}
              >
                Transaction ID :{" "}
                <span style={{ fontSize: "14px", fontWeight: "400" }}>
                  {transactionId}
                </span>
              </p>
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
            }}
          >
            <ActionButton
              label="View Payment Receipt"
              isSellerPortal={isSellerPortal()}
              outlined
              onClick={showPaymentReceipt}
              disabled={isOrderCancelled}
              sx={{
                color: colors.blackButton,
                borderColor: colors.blackButton,
              }}
            />
            <ActionButton
              outlined
              label="Trigger Payment"
              isSellerPortal={isSellerPortal()}
              onClick={() => setShowPopUp(true)}
              disabled={
                isOrderCancelled || (currentStage >= 3 && !!transactionId)
              }
            />
          </div>
        </Box>
      )}

      {customerNote && (
        <span
          style={{
            fontSize: "14px",
            fontWeight: 400,
            color: "#555",
            marginLeft: "20px",
            paddingLeft: "12px",
            borderLeft: "1px solid #ccc",
            lineHeight: "1.6",
            display: "block",
            marginTop: "8px",
            marginBottom: "8px",
          }}
        >
          <b>Note:</b> {customerNote}
        </span>
      )}

      {/* Modal to show the receipt image */}
      <Modal
        open={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        aria-labelledby="receipt-modal-title"
        aria-describedby="receipt-modal-description"
        role="dialog"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          {/* Close Button */}
          <IconButton
            data-testid="cross-icon"
            onClick={() => setShowReceiptModal(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 100,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Payment Receipt
          </Typography>

          {receiptUrl ? (
            <Box
              sx={{ width: "100%", display: "flex", justifyContent: "center" }}
            >
              {receiptUrl.toLowerCase().includes(".pdf") ? (
                <iframe
                  src={receiptUrl}
                  title="Payment Receipt"
                  style={{
                    width: "100%",
                    height: "80vh",
                    border: "none",
                  }}
                />
              ) : (
                <img
                  src={receiptUrl}
                  alt="Payment Receipt"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>
          ) : (
            <Typography sx={{ mt: 2 }}>Loading receipt...</Typography>
          )}
        </Box>
      </Modal>
      <ConfirmationDialog
        buttonAction={triggerPayment}
        buttonTitle="Yes, I Am Sure"
        show={showPopUp}
        setShow={setShowPopUp}
        title="Are you sure you want to Trigger Payment?"
      />
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />
    </Card>
  );
};

export default OrderDetails;
